import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { TURNSTILE_SITE_KEY } from '../../constants';

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise = null;

const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error('No se pudo cargar la verificación de seguridad.'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
};

/**
 * Captcha de Cloudflare Turnstile. Llama a onToken con el token válido, o con ''
 * cuando expira o falla. El token es de un solo uso: llamar reset() tras cada envío.
 */
export const TurnstileWidget = forwardRef(({ onToken, onError }, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const callbacksRef = useRef({ onToken, onError });
  callbacksRef.current = { onToken, onError };

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.reset(widgetIdRef.current);
      }
      callbacksRef.current.onToken('');
    },
  }));

  useEffect(() => {
    let cancelled = false;

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          language: 'es',
          callback: (token) => callbacksRef.current.onToken(token),
          'expired-callback': () => callbacksRef.current.onToken(''),
          'error-callback': () => {
            callbacksRef.current.onToken('');
            callbacksRef.current.onError?.('Falló la verificación de seguridad. Recargá la página.');
          },
        });
      })
      .catch((error) => {
        if (!cancelled) callbacksRef.current.onError?.(error.message);
      });

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, []);

  return <div ref={containerRef} className="min-h-[65px]" />;
});

TurnstileWidget.displayName = 'TurnstileWidget';
